import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import { useActiveProject } from '../../context/ActiveProjectContext';
import { buildProjectUrl, isStaffRole, STAFF_ROLES } from '../../utils/deepLink';
import api from '../../utils/api';
import { normalizeClientWorkspaceTab } from '../cycle/ClientWorkspaceTabGuard';

export { STAFF_ROLES, isStaffRole };

/** ProjectDetail tab hashes for staff (not client nested paths). */
const STAFF_PROJECT_HASH = {
  cycles: 'cycles',
  defects: 'issues',
  test_cases: 'repository',
  integrations: 'integrations',
  forms: 'forms',
};

export function staffProjectHashPath(projectId, section) {
  const hash = STAFF_PROJECT_HASH[section] || section;
  return `/app/projects/${projectId}#${hash}`;
}

export function StaffProjectSectionRedirect({ section }) {
  const { id } = useParams();
  const { user } = useSelector((state) => state.auth);
  if (!id) return <Navigate to="/app/projects" replace />;
  if (isStaffRole(user?.role)) {
    return <Navigate to={staffProjectHashPath(id, section)} replace />;
  }
  return <Navigate to={buildProjectUrl(id, section)} replace />;
}

function useClientPid() {
  const [searchParams] = useSearchParams();
  const { activeProjectId } = useActiveProject();
  return searchParams.get('projectId') || activeProjectId || '';
}

export function ClientLegacyTabRedirect({ tab }) {
  const pid = useClientPid();
  if (!pid) return <Navigate to="/app/projects" replace />;
  return <Navigate to={buildProjectUrl(pid, tab)} replace />;
}

export function ClientDashboardRedirect() {
  const { user } = useSelector((state) => state.auth);
  const { activeProjectId, projects, loading } = useActiveProject();

  if (user?.role !== 'client' && user?.role !== 'client_team') {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (activeProjectId) {
    return <Navigate to={`/app/projects/${activeProjectId}`} replace />;
  }
  if (projects.length === 1) {
    return <Navigate to={`/app/projects/${projects[0].id}`} replace />;
  }
  return <Navigate to="/app/projects" replace />;
}

export function ClientLegacyNewCycleRedirect() {
  const { id } = useParams();
  if (!id) return <Navigate to="/app/projects" replace />;
  return <Navigate to={`/app/projects/${id}/cycles/new`} replace />;
}

export function ClientLegacyWorkspaceRedirect({ defaultTab = 'setup' } = {}) {
  const { cycleId, id } = useParams();
  const resolvedCycleId = cycleId || id;
  const location = useLocation();
  const rawTab = location.pathname.split('/workspace/')[1]?.split('/')[0] || defaultTab;
  const tab = normalizeClientWorkspaceTab(rawTab);
  const [target, setTarget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/test-cycles/${resolvedCycleId}`);
        const cycle = res.data?.testCycle || res.data;
        const pid = cycle?.projectId || cycle?.project?.id;
        if (!cancelled && pid) {
          setTarget(`/app/projects/${pid}/cycles/${resolvedCycleId}/workspace/${tab}`);
        } else if (!cancelled) {
          setTarget('/app/projects');
        }
      } catch {
        if (!cancelled) setTarget('/app/projects');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resolvedCycleId, tab]);

  if (!target) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  return <Navigate to={target} replace />;
}
